'use client';

import React, { useLayoutEffect, useState } from 'react';
import styled from '@benzinga/themetron';
import { usePermission } from '@benzinga/user-context';
import { RankingDetail } from '@benzinga/quotes-manager';
import { usePaywallText } from './utils';
import { EdgeRankingPaywallButton } from './EdgeRankingPaywallButton';
import { RankingItem } from './RankingItem';
import { PriceTrendItem } from './PriceTrendItem';

interface EdgeRankingQuoteLayoutProps extends RankingDetail {
  className?: string;
  showRanking?: boolean;
  stacked?: boolean;
  symbol: string;
  adType: string;
}

export const EdgeRankingQuoteLayout: React.FC<EdgeRankingQuoteLayoutProps> = ({
  adType,
  className = '',
  growth,
  longTermTrend = 'Y',
  mediumTermTrend = 'Y',
  momentum,
  quality,
  shortTermTrend = 'Y',
  stacked = false,
  value,
}) => {
  const [show, setShow] = useState(false);
  const hasPermission = usePermission('com/read', 'unlimited-calendars');

  useLayoutEffect(() => {
    if (hasPermission) {
      setShow(true);
    }
  }, [hasPermission]);

  if (!show) {
    return <GetAccessToEdgeRanking adType={adType} stacked={stacked} />;
  }

  return (
    <EdgeRankingQuoteLayoutWrapper className={className}>
      <div className={`ranking-wrap flex flex-col w-full ${stacked ? 'stacked' : 'md:flex-row'}`}>
        <div className="rankings-data-box flex flex-col justify-end bg-wrap w-full p-4 rounded border border-bzblue-400">
          <h5 className="leading-none mb-2">Benzinga Rankings</h5>
          <div className="ranking-row flex flex-row w-full">
            {RankingItem({ label: 'Momentum', value: momentum ?? 0 })}
            {RankingItem({ label: 'Value', value: value ?? 0 })}
          </div>
          <div className="ranking-row flex flex-row">
            {RankingItem({ label: 'Growth', value: growth ?? 0 })}
            {RankingItem({ label: 'Quality', value: quality ?? 0 })}
          </div>
        </div>
        <div className="price-trend-box w-full flex flex-col bg-wrap h-full min-h-[106px] justify-center p-4 rounded border border-bzblue-400">
          <h5 className="leading-none mb-2">Price Trend</h5>
          <div className={`flex gap-2 flex-col ${stacked ? '' : 'sm:flex-row'}`}>
            <PriceTrendItem label="Short" trend={shortTermTrend ?? ''} />
            <PriceTrendItem label="Medium" trend={mediumTermTrend ?? ''} />
            <PriceTrendItem label="Long" trend={longTermTrend ?? ''} />
          </div>
        </div>
      </div>
    </EdgeRankingQuoteLayoutWrapper>
  );
};

export default EdgeRankingQuoteLayout;

const GetAccessToEdgeRanking = ({ adType, stacked }: { adType: string; stacked: boolean }) => {
  const [paywallLabel, paywallDesc, paywallButtonLabel, buyNowLink, checkoutAd] = usePaywallText(adType);

  return (
    <GetEdgeRankingWrap className={`get-access-wrap ${stacked ? 'stacked' : ''}`}>
      <div
        className={`flex flex-col justify-between w-full gap-2 ${stacked ? 'items-stretch' : 'items-center sm:flex-row'}`}
      >
        <div className="access-header">
          <h3 className="ranking-paywall-heading">{paywallLabel}</h3>
          <p className="ranking-paywall-description">{paywallDesc}</p>
        </div>
        <EdgeRankingPaywallButton
          adType={adType}
          checkoutAd={checkoutAd}
          checkoutUrl={buyNowLink}
          className={`w-full ranking-paywall-button ${stacked ? '' : 'sm:w-auto'}`}
          label={paywallButtonLabel}
        />
      </div>
    </GetEdgeRankingWrap>
  );
};

const GetEdgeRankingWrap = styled.div`
  background-color: #f2f8ff;
  border: 1px solid #ceddf2;
  border-radius: 4px;
  padding: 1rem;
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  a {
    min-width: 140px;
    padding: 12px 10px;
    @media (max-width: 768px) {
      min-width: 140px;
      padding: 12px 10px;
    }
  }
  .access-header {
    h3 {
      font-size: 1.2rem;
      color: #192940;
    }
    p {
      color: #5b7292;
      font-size: 14px;
    }
  }
  button {
    font-size: 16px;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  &.stacked {
    padding: 0;
    border: 0;
    background: transparent;
    .access-header h3 {
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: #ffffff;
    }
    .access-header p {
      font-size: 0.875rem;
      line-height: 1.45;
      color: #cbd5e1;
    }
    button {
      font-size: 14px;
    }
  }
`;

const EdgeRankingQuoteLayoutWrapper = styled.div`
  .ranking-wrap {
    flex-grow: 1;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    width: 100%;
    .bg-wrap {
      background-color: #f2f8ff;
    }
    h5 {
      font-size: 16px;
      font-weight: 600;
      color: rgb(37, 50, 67);
    }
    .rankings-data-box {
      .ranking-row {
        .ranking-item {
          margin-right: 0.8rem;
          display: flex;
          width: 100%;
          .ranking-label {
            color: #5b7292;
            min-width: 110px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .ranking-value {
            font-size: 16px;
            min-width: 40px;
            font-weight: 600;
          }
        }
        .ranking-item {
          &:nth-child(2) {
            .ranking-label {
              min-width: 75px;
            }
          }
        }
      }
    }
    &.stacked .bg-wrap {
      min-height: 0;
      padding: 0;
      border: 0;
      background: transparent;
    }
    &.stacked .price-trend-item {
      padding: 2px 0;
    }
    &.stacked .rankings-data-box .ranking-row {
      flex-direction: column;
      .ranking-item {
        margin-right: 0;
        padding: 2px 0;
        justify-content: space-between;
        .ranking-label {
          min-width: 0;
        }
        .ranking-value {
          min-width: 0;
          text-align: right;
        }
      }
    }
    .price-trend-box {
      .price-trend-item {
        display: flex;
        border-radius: 4px;
        padding: 4px 6px;
        align-items: center;
        width: 100%;
        justify-content: space-between;
        background: white;

        .position-label {
          color: #5b7292;
          font-weight: 600;
        }
        .position-direction {
          height: 30px;
          width: 30px;
          justify-content: center;
          display: flex;
          align-items: center;
          padding: 0.5rem;
          border-radius: 4px;

          &.positive {
            background: #30bf6040;
          }
          &.negative {
            background: #ff405040;
          }
        }
      }
    }
  }
`;
